import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { LOGO_URL } from "@/lib/api";

const LINKS = [
  { id: "about", label: "About", testid: "nav-link-about" },
  { id: "founder", label: "Founders", testid: "nav-link-founder" },
  { id: "offerings", label: "Offerings", testid: "nav-link-offerings" },
  { id: "courses", label: "Courses", testid: "nav-link-courses" },
  { id: "live", label: "Live Workshop", testid: "nav-link-live" },
];

export default function Navbar({ content }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const formUrl = content?.workshop_form_url;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const registerCls =
    "group flex items-center gap-2 bg-cherry hover:bg-[#B81919] text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors duration-300";
  const registerIcon = (
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  );

  const registerBtn = (testid) =>
    formUrl ? (
      <a href={formUrl} target="_blank" rel="noopener noreferrer" data-testid={testid} className={registerCls}>
        Register Now {registerIcon}
      </a>
    ) : (
      <button data-testid={testid} onClick={() => go("live")} className={registerCls}>
        Register Now {registerIcon}
      </button>
    );

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-navy/90 backdrop-blur-xl border-b border-white/10 py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3" data-testid="nav-brand-logo">
          <img src={content?.logo_url || LOGO_URL} alt="ScholarVerse LAB logo" className="h-10 w-10 rounded-full bg-white object-cover" />
          <span className="font-serif text-xl font-bold text-white tracking-tight">
            ScholarVerse <span className="text-cherry">LAB</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-sans text-white/75 hover:text-white transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to="/admin" data-testid="nav-admin-link" className="text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white transition-colors">
            Admin
          </Link>
          {registerBtn("nav-register-button")}
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-navy/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-white/85 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex items-center gap-4 pt-2">
                {registerBtn("mobile-nav-register-button")}
                <Link to="/admin" data-testid="mobile-nav-admin-link" className="text-xs font-mono uppercase tracking-widest text-white/50">
                  Admin
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
